import Swiper from 'swiper';
import { Navigation, Pagination, Autoplay } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/pagination';

var cards_swipers = [];

const BASE_BREAKPOINTS = {
    0:    { slidesPerView: 1.25, spaceBetween: 11, centeredSlidesBounds: false, initialSlide: 1 },
    640:  { slidesPerView: 2,    spaceBetween: 14, centeredSlidesBounds: true },
    768:  { slidesPerView: 2,    spaceBetween: 15, centeredSlidesBounds: true },
    900:  { slidesPerView: 3,    spaceBetween: 16, centeredSlidesBounds: true },
    1024: { slidesPerView: 3,    spaceBetween: 18, centeredSlidesBounds: true },
    1200: { slidesPerView: 3,    spaceBetween: 20, centeredSlidesBounds: true },
};

// Largest slidesPerView across all breakpoints (rounded up, since values
// can be fractional like 1.25).
const MAX_SLIDES_PER_VIEW = Math.ceil(
    Math.max(...Object.values(BASE_BREAKPOINTS).map(bp => bp.slidesPerView))
);

// Finds the active breakpoint config for a given viewport width, matching
// Swiper's own "largest key <= current width" resolution behaviour.
function getActiveBreakpointConfig(width) {
    const keys = Object.keys(BASE_BREAKPOINTS).map(Number).sort((a, b) => a - b);
    let active = BASE_BREAKPOINTS[keys[0]];
    for (const key of keys) {
        if (width >= key) active = BASE_BREAKPOINTS[key];
    }
    return active;
}

// Pagination should only show when real slides outnumber the current
// breakpoint's slidesPerView. Uses a CSS class (not inline style) with
// !important so it can't be silently overridden by Swiper's own pagination
// CSS or theme styles run during/after Swiper's init.
function updatePaginationVisibility(paginationEl, cardsEl, realSlideCount, forceShowPagination, forceHidePagination) {
    if (!paginationEl) return;

    if (forceHidePagination) {
        paginationEl.classList.add('pagination-force-hidden');
        cardsEl?.classList.remove('has-pagination');
        cardsEl?.classList.add('no-pagination');
        return;
    }

    const { slidesPerView } = getActiveBreakpointConfig(window.innerWidth);
    const shouldShow = forceShowPagination || realSlideCount > slidesPerView;

    paginationEl.classList.toggle('pagination-force-hidden', !shouldShow);
    cardsEl?.classList.toggle('has-pagination', shouldShow);
    cardsEl?.classList.toggle('no-pagination', !shouldShow);
}

function initialiseSwipers()
{
    cards_swipers.forEach(cards_swiper =>
    {
        if (cards_swiper instanceof Swiper)
        {
            cards_swiper.destroy();
        }
    });

    document.querySelectorAll('section.testimonials .testimonials_cards').forEach((cards) => {
    const parent_section = cards.closest("section");
    const swiperEl = cards.querySelector(".swiper");
    const wrapper = swiperEl?.querySelector(".swiper-wrapper");
    const realSlideCount = wrapper
        ? wrapper.querySelectorAll(".swiper-slide").length
        : 0;

    if (!swiperEl || realSlideCount === 0) {
        cards.remove();
        return;
    }

    // Whether there are more real slides than fit in the widest view —
    // used for arrows and loop eligibility, which stay fixed regardless
    // of current viewport width.
    const hasOverflow = realSlideCount > MAX_SLIDES_PER_VIEW;

    // Loop needs at least 2x the widest slidesPerView in real slides to build
    // clones cleanly. With fewer than that, loop mode visually breaks.
    const minSlidesForLoop = MAX_SLIDES_PER_VIEW * 2;
    const canLoop = realSlideCount >= minSlidesForLoop;

    let autoplay = false;
    if (canLoop && parent_section.classList.contains("autoplay")) {
        autoplay = { delay: 1600 };
    }

    // CMS-driven overrides. "show_*" forces the control on regardless of
    // overflow; "hide_*" forces it off regardless of overflow/show.
    const forceShowArrows = parent_section.classList.contains("show_navigation_arrows_outside");
    const forceHideArrows = parent_section.classList.contains("hide_navigation_arrows");
    const forceShowPagination = parent_section.classList.contains("show_pagination_dots");
    const forceHidePagination = parent_section.classList.contains("hide_pagination_dots");

    if ((!hasOverflow && !forceShowArrows) || forceHideArrows) {
        cards.querySelector(".swiper-button-prev")?.remove();
        cards.querySelector(".swiper-button-next")?.remove();
    }

    const paginationEl = cards.querySelector('.swiper-pagination');

    if (canLoop) {
        padSlides(swiperEl, minSlidesForLoop);
    }
    initialiseReadMore(swiperEl);

    const swiper_options = {
        modules: [Navigation, Pagination, Autoplay],
        direction: 'horizontal',
        loop: canLoop,
        autoplay: autoplay,
        speed: 700,
        spaceBetween: 15,
        slidesPerView: 'auto',
        centeredSlides: true,
        centeredSlidesBounds: true,
        loopedSlides: canLoop ? minSlidesForLoop : undefined,
        navigation: {
            nextEl: parent_section.querySelector(".swiper-button-next"),
            prevEl: parent_section.querySelector(".swiper-button-prev"),
        },
        pagination: {
            el: paginationEl,
            clickable: true,
        },
        breakpoints: BASE_BREAKPOINTS,
    };

    const swiperInstance = new Swiper(swiperEl, swiper_options);

    // Run after Swiper has fully initialized (and re-run on breakpoint/resize)
    // so our class isn't clobbered by Swiper's own pagination rendering.
    const syncPagination = () => {
        updatePaginationVisibility(paginationEl, cards, realSlideCount, forceShowPagination, forceHidePagination);
    };

    swiperInstance.on('init', syncPagination);
    swiperInstance.on('breakpoint', syncPagination);
    swiperInstance.on('resize', syncPagination);

    // Swiper's 'init' event only fires for instances created with init:false,
    // so call it directly here too since this instance already auto-initialized.
    syncPagination();

    cards_swipers.push(swiperInstance);
});
}

function initialiseReadMore(swiperEl) {
    swiperEl.querySelectorAll('.testimonials_cards_quote').forEach((quote) => {
        const item = quote.closest('.testimonials_cards_item');
        const button = item?.querySelector('.testimonials_cards_quote_more');
        if (!button) return;

        button.hidden = quote.scrollHeight <= quote.clientHeight + 1;

        button.addEventListener('click', () => {
            const expanded = item.classList.toggle('expanded');
            quote.classList.toggle('expanded', expanded);
            button.textContent = expanded ? 'Less' : 'More';

            swiperEl.swiper?.update();
        });
    });
}

export const padSlides = function(swiperEl, minSlides = 12) {
    const wrapper = swiperEl.querySelector(".swiper-wrapper");
    if (!wrapper) return;

    let slides = Array.from(wrapper.children).filter((n) =>
        n.classList.contains("swiper-slide")
    );

    if (slides.length === 0 || slides.length >= minSlides) return;

    while (slides.length < minSlides) {
        slides.forEach((slide) => {
            const node = slide.cloneNode(true);
            node.querySelector('img')?.classList?.add('loaded');
            wrapper.appendChild(node)
        });
        slides = Array.from(wrapper.children).filter((n) =>
            n.classList.contains("swiper-slide")
        );
    }
}

document.addEventListener("DOMContentLoaded", () => {
    initialiseSwipers();
});